import type { LucideProps } from 'lucide-react'
import { ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { cn } from 'src/lib/utils'
import { BuildingFilled } from 'src/shared/components/icons/building-filled'
import { Iconmark } from 'src/shared/components/icons/logos/iconmark'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/popups/dropdown-menu'
import { useSignOut } from 'src/shared/features/auth/api'
import useGetEnterprise from 'src/shared/features/enterprise/api/use-get-enterprise'
import SupportDialogV2 from 'src/shared/features/support/components/support-dialog-v2'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import ProfileAvatar from 'src/shared/features/users/components/profile-photos/profile-avatar'

import { DirectoryIcon } from '../icons/directory-icon'
import { HelpCenterIcon } from '../icons/help-center-icon'
import { PipelineIcon } from '../icons/pipeline-icon'
import { SettingsIcon } from '../icons/settings-icon'

import TabBarButton from './pro-app-frame-tab-button'
import TabIconButton from './pro-app-frame-tab-icon-button'

const TAB_BAR_BUTTONS = [
  // {
  //   title: 'Home',
  //   key: 'home',
  //   path: paths.enterprise.dashboard.path,
  //   exact: true,
  //   icon: (props: LucideProps) => <House size={18} {...props} />,
  // },
  {
    title: 'Directory',
    key: 'directory',
    path: paths.enterprise.dashboard.directory.path,
    icon: (props: LucideProps) => <DirectoryIcon size={20} {...props} />,
  },
  {
    title: 'Pipeline',
    key: 'pipeline',
    path: paths.enterprise.dashboard.pipeline.path,
    icon: (props: LucideProps) => <PipelineIcon size={15} {...props} />,
  },
  // {
  //   title: 'Pipeline',
  //   key: 'pipeline',
  //   icon: (props: LucideProps) => <Workflow size={18} {...props} />,
  // },
  // {
  //   title: 'Billing',
  //   key: 'billing',
  //   icon: (props: LucideProps) => <CreditCard size={18} {...props} />,
  // },
]

const EnterpriseAppFrameHeader = () => {
  const [supportDialogOpen, setSupportDialogOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { data: currentUser } = useMyUserData()
  const { data: enterprise } = useGetEnterprise({ enterpriseId: currentUser?.enterpriseId ?? -1 })
  const signOut = useSignOut({ isEnterprise: true })

  const enterpriseIcon = enterprise?.iconPicture
  const enterpriseName = enterprise?.name

  return (
    <div id="header" className="flex h-[70px] flex-row items-center justify-between px-[30px]">
      <div className="flex shrink-0 flex-row items-center gap-5">
        <Link to={paths.enterprise.dashboard.getHref()} className="flex shrink-0 items-center justify-center">
          <Iconmark className="size-[28px] text-white md:size-[30px]" />
        </Link>
        <div className="flex flex-row items-center gap-2.5">
          {TAB_BAR_BUTTONS.map((button) => (
            <TabBarButton
              key={button.key}
              icon={button.icon}
              title={button.title}
              onClick={() => button.path && navigate(button.path)}
              isActive={
                button.path
                  ? 'exact' in button && button.exact
                    ? location.pathname === button.path
                    : location.pathname === button.path || location.pathname.startsWith(button.path + '/')
                  : false
              }
            />
          ))}
        </div>
      </div>
      <div className="flex shrink-0 flex-row items-center gap-2.5">
        <TabIconButton
          key="settings"
          icon={(props: LucideProps) => <SettingsIcon size={20} {...props} />}
          onClick={() => navigate(paths.enterprise.dashboard.settings.path)}
          isActive={location.pathname.startsWith(paths.enterprise.dashboard.settings.path)}
        />
        {/* <AllNotificationsModal
          open={showNotificationModal}
          trigger={
            <div className="relative">
              <TabIconButton
                key="activity"
                onClick={() => {}}
                icon={(props: LucideProps) => <ActivityIcon size={22} {...props} />}
                isActive={showNotificationModal}
              />
              <NotificationCountBubble />
            </div>
          }
          onOpenChange={setShowNotificationModal}
        /> */}
        <TabIconButton
          key="help-center"
          icon={(props: LucideProps) => <HelpCenterIcon size={20} {...props} />}
          onClick={() => setSupportDialogOpen(true)}
          isActive={supportDialogOpen}
        />
        <SupportDialogV2 open={supportDialogOpen} setOpen={setSupportDialogOpen} />

        {/* Profile Picture Trigger and Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger className="focus-visible:outline-none">
            <div className="flex shrink-0 cursor-pointer flex-row items-center gap-3">
              <div className="relative">
                <div
                  className={cn(
                    'box-content flex size-[39px] shrink-0 items-center justify-center rounded-full border-[2px] border-white bg-muted',
                    enterpriseIcon && 'bg-accent',
                  )}
                >
                  {enterpriseIcon ? (
                    <img src={enterpriseIcon} alt={`${enterpriseName} icon`} className="size-full object-cover" />
                  ) : (
                    <BuildingFilled size={18} className="text-[#D8D8D6]" />
                  )}
                </div>
                <div className="pointer-events-none absolute bottom-0 right-0 translate-x-1/4 translate-y-1/4 select-none">
                  <ProfileAvatar
                    size={20}
                    userId={currentUser?.id ?? -1}
                    className="box-content shrink-0 border-[1.5px] border-white"
                  />
                </div>
              </div>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            sideOffset={4}
            align="end"
            className="border-secondaryBorder mt-1 w-[250px] flex-col items-start gap-0 rounded-[20px] p-[14px] shadow-[0px_0px_25px_0px_rgba(0,0,0,0.1)]"
          >
            <DropdownMenuItem
              onClick={() => signOut.mutateAsync()}
              className="group/item flex h-[40px] w-full cursor-pointer items-center justify-between rounded-[12px] bg-transparent px-[15px] py-[5px] hover:bg-accent"
            >
              <p className="text-[14px] font-semibold leading-[1.35] tracking-[-0.14px] text-primary">Log out</p>
              <ChevronRight size={10} className="text-primary opacity-0 group-hover/item:opacity-100" />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

export default EnterpriseAppFrameHeader
